import axios from 'axios'
import toast from 'react-hot-toast'
import posthog from 'posthog-js'
import config from '../config'
import { TOKEN, RMN_SLUG } from '../store/constant'
import { clearLocalStorageKeepingLastUsed } from '../utils/lastSignInMethod'

const axiosInstant = axios.create({ baseURL: config.API_URL as string })

// Client-side ceilings so a request can never hang a form forever (the
// confirmation form's Submit sat on "Submitting..." for minutes). The API
// itself answers 503 RequestTimeout at 30s, or 180s on the long paths below
// (mirrors LONG_TIMEOUT_* in desirelist-api application/src/app.js), so these
// sit just above that. A caller-provided `timeout` always wins; multipart
// uploads and blob downloads are exempt because their duration is the
// payload's, not the server's.
const DEFAULT_TIMEOUT_MS = 45000
const LONG_TIMEOUT_MS = 190000
const LONG_TIMEOUT_PATHS = [
  '/public/audit/pdp',
  '/admin/sales/prep-packs',
  '/rmn/audit/pdp',
  '/rmn/prep-packs',
  '/users/prosCsv',
  '/brand-orders/intake/parse',
  '/admin/scores/demo/url',
]
const LONG_TIMEOUT_PREFIXES = ['/admin/sales/prep-packs/', '/rmn/prep-packs/']
const LONG_TIMEOUT_REGEXES = [
  /^\/accountmanager\/products\/\d+\/pdp-urls\/\d+\/score$/,
  /^\/accountmanager\/brands\/\d+\/onboarding\/check$/,
]

const isLongRequest = (url: string | undefined) => {
  if (!url) return false
  const path = url.split('?')[0]
  return (
    LONG_TIMEOUT_PATHS.some((p) => path === p) ||
    LONG_TIMEOUT_PREFIXES.some((p) => path.startsWith(p)) ||
    LONG_TIMEOUT_REGEXES.some((r) => r.test(path))
  )
}

const isTimeoutExempt = (axiosConfig: { data?: unknown; responseType?: string }) =>
  (typeof FormData !== 'undefined' && axiosConfig.data instanceof FormData) ||
  axiosConfig.responseType === 'blob' ||
  axiosConfig.responseType === 'arraybuffer'

// Active portal brand id, registered by RmnBrandLayout while the user is
// inside /portal/:brandSlug/*. The API's rmnControl middleware resolves brand
// users from their memberships but requires internal staff to name the
// network via ?rmn_brand_id=, so the interceptor adds it to every
// authenticated /rmn/* request (the API ignores it for brand users).
let activeRmnBrandId: number | null = null
export const setActiveRmnBrandId = (id: number | null) => {
  activeRmnBrandId = id
}

// This tab's copy of the login token, kept beside localStorage. On iPhones a
// token stored in the first second after an email-link sign-in (/redirect)
// can vanish from localStorage seconds later while the page still shows the
// expert logged in: every request after that went out anonymous, the MSA tab
// said "no permission" and Save sent them to /login, losing their edits
// (PostHog issue 01a0a059 follow-up, 2026-10-30). Neither our code nor the
// page's vendor scripts clear it, so requests fall back to this copy and the
// user store writes the session back.
let sessionToken: string | null = localStorage.getItem(TOKEN)
let restoreStoredSession: ((token: string) => void) | null = null
// One auth_token_restored per session: while the browser keeps dropping the
// token (or the write-back fails), every request restores it again.
let restoreReported = false
export const setSessionToken = (token: string | null) => {
  sessionToken = token
  restoreReported = false
}
export const onRestoreStoredSession = (restore: (token: string) => void) => {
  restoreStoredSession = restore
}

// A login or logout in another tab is deliberate: follow it instead of
// restoring this tab's copy over it. `storage` only fires for changes made by
// other documents, and clear() arrives with a null key.
window.addEventListener('storage', (e) => {
  if (e.storageArea === localStorage && (e.key === TOKEN || e.key === null)) {
    sessionToken = e.newValue
  }
})

const currentToken = (): string | null => {
  const stored = localStorage.getItem(TOKEN)
  if (stored || !sessionToken) return stored
  restoreStoredSession?.(sessionToken)
  if (!restoreReported) {
    restoreReported = true
    // No token or user data in the event; PostHog adds browser and page.
    posthog.capture('auth_token_restored')
  }
  return sessionToken
}

// The `exp` claim of the stored JWT, in ms — or null when the token is absent
// or not decodable (then the API decides). Sessions are 7-day tokens that the
// portal never refreshes, so a tab reopened after a week carries a dead token.
const tokenExpiryMs = (token: string | null): number | null => {
  if (!token) return null
  try {
    const payload = token.split('.')[1]
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const exp = JSON.parse(json)?.exp
    return typeof exp === 'number' ? exp * 1000 : null
  } catch (e) {
    return null
  }
}

// Top-level routes of router/default.tsx that render without a session.
const PUBLIC_PAGE_RE = new RegExp(
  '^/(experts/apply|experts/share|pdp-audit|consumer-voice-audit|ai-presence-audit|proposal|proposal/view/[^/]+|' +
    'projects/(share|public)/[^/]+|orders/(share|public|feedback)/[^/]+|videos/shortlists/share/[^/]+|' +
    'login|auth/google|register(/[^/]+)?|forgot-password(-submitted(/[^/]*)?)?|reset-password|404|403|portal/[^/]+/login)/?$',
)

axiosInstant.interceptors.request.use((axiosConfig) => {
  const token = currentToken()
  // An expired token is not "anonymous": the API's userTokenCheck is soft and
  // downgrades it to an anonymous caller, so endpoints with a public shape
  // (e.g. GET /brand-projects/:id serving the share stub) answer 200 with
  // data the logged-in page cannot render — the project page crashed on the
  // stub's missing status and the error boundary showed a 404 (Slack deep
  // links "404ing", 2026-09). Only strict endpoints 401 into the logout
  // flow. Decide it here, before the first request of the tab leaves, so
  // every page goes to /login?redirect=<this deep link> instead.
  // Public pages (share links, audits, auth screens) render for anonymous
  // visitors, so there the dead token is simply not sent; an endpoint that
  // really needs a session still 401s into the logout flow below.
  const expiresAt = tokenExpiryMs(token)
  const expired = expiresAt !== null && expiresAt <= Date.now()
  if (expired && !PUBLIC_PAGE_RE.test(window.location.pathname)) {
    logoutUser()
    return sessionEnded(new axios.Cancel('Session expired'))
  }
  const nextConfig = token && !expired
    ? {
        ...axiosConfig,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    : { ...axiosConfig }
  if (activeRmnBrandId && axiosConfig.url?.startsWith('/rmn/')) {
    // Caller-provided params win over the injected network id.
    nextConfig.params = { rmn_brand_id: activeRmnBrandId, ...(axiosConfig.params || {}) }
  }
  if (!axiosConfig.timeout && !isTimeoutExempt(axiosConfig)) {
    nextConfig.timeout = isLongRequest(axiosConfig.url) ? LONG_TIMEOUT_MS : DEFAULT_TIMEOUT_MS
  }
  return nextConfig
})

// What a request ended by logoutUser() hands back. logoutUser() has already
// sent the browser to the login page, so the request is left pending instead
// of rejected: the page is unloading, and a rejection no caller catches is the
// unhandled "Request failed with status code 401" PostHog reports (TDC-1776).
// A Cancel would only rename it; nothing filters those either. Public pages
// keep the rejection so their own forms can show it (an unknown login email
// answers 401 NoSuchUser).
// If the page is still here after the grace period, the redirect never
// happened (the user chose "Stay" on a leave-page prompt), so the request
// rejects after all: a caller waiting on it (a Save button) gets its error
// path instead of hanging forever.
export const SESSION_END_GRACE_MS = 3000
const sessionEnded = (error: unknown): Promise<never> => {
  if (PUBLIC_PAGE_RE.test(window.location.pathname)) return Promise.reject(error)
  return new Promise<never>((_resolve, reject) => {
    const timer = setTimeout(() => reject(error), SESSION_END_GRACE_MS)
    window.addEventListener('pagehide', () => clearTimeout(timer), { once: true })
  })
}

const logoutUser = () => {
  // Read before clear(): the brand slug decides which login page (branded vs
  // default) the expired session returns to.
  const rmnSlug = localStorage.getItem(RMN_SLUG);
  // The session is over: nothing may restore it (e.g. a page brought back
  // with the Back button after this redirect).
  sessionToken = null;
  clearLocalStorageKeepingLastUsed();

  // Capture the current page the user was trying to access
  const currentPath = window.location.pathname + window.location.search;
  const redirectParam = encodeURIComponent(currentPath);

  // No longer requires the current path to be slugged (TDC-1609). A client's
  // personal pages (/settings/profile) stay at the root, and a 401 fired from
  // one of those must still return them to their own branded login.
  const loginPath = rmnSlug ? `/portal/${rmnSlug}/login` : '/login';
  window.location.href = `${loginPath}?redirect=${redirectParam}`;
}

// 403 codes a page handles itself (with its own message/redirect), so the
// global permission toast stays quiet for them.
const LOCALLY_HANDLED_403_CODES = [
  'MsaNotSigned',
  // Google sign-in / Sign-in methods refusals, shown by their own pages.
  'GoogleNotAvailableForAccount',
  'PersonalKeyNotAllowed',
  'BadOrigin',
]

const notifyForbidden = (data: unknown) => {
  // Gate responses send { error: 'Forbidden' }; ApiErrors unwrapped by the
  // API's control middleware arrive as a bare JSON string.
  const code = typeof data === 'string' ? data : (data as { error?: string } | null)?.error
  if (code && LOCALLY_HANDLED_403_CODES.includes(code)) return
  // Fixed id: parallel 403s replace the toast instead of stacking it.
  toast.error("You don't have permission to do that.", { id: 'forbidden-403' })
}

axiosInstant.interceptors.response.use((response) => {
  if(response.status === 401) logoutUser();
  return response;
}, (error) => {
  if (error?.response?.status === 401) {
    logoutUser();
    return sessionEnded(error);
  }
  if (error?.response?.status === 403) notifyForbidden(error.response.data);
  return Promise.reject(error)
});

export default axiosInstant
